import { Injectable, Pipe, PipeTransform, inject } from '@angular/core';
import { ApiService } from './../../../services/apiservice/apiservice.service';
import { ConfigService } from '../../../services/config/config.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { LibraryService, Location, UserLocation } from '../../../services/library/library.service';
import { HtmlField } from '../../../components/dynamic-form/dynamic-form.service';
import { StaffMenuItem } from '../../dashboard/dashboard.service';
import { ODataQuery, ODataSearchableField } from '../../../services/odata/odata.service';
import { Library, LibraryInfo } from '../../../core/interfaces/library.interface';
import { filter, map } from 'rxjs/operators';

@Pipe({
  name: 'cloneUserRoleDisplay',
  standalone: false,
})
export class CloneUserRolesDisplayPipe implements PipeTransform {
  transform(roles: string[], userRoles: UserRole[]): CloneUserRole[] {
    return roles.map((role) => {
      return {
        role,
        label: userRoles.find((r) => r.role === role)?.label ?? role,
        cloneable: userRoles.some((r) => r.role === role),
      };
    });
  }
}
export interface CloneUserRole extends UserRole {
  cloneable: boolean;
}

export interface UserRole {
  role: string;
  label: string;
}

export interface UserRoleAccess extends UserRole {
  hasRole: boolean;
  editable: boolean;
}

export interface MenuPermission {
  label: string;
  menuItemId: number;
  hasPermission: boolean;
  subMenuPermissions: MenuPermission[];
  editable: boolean;
}

export interface UserFeatureSettings {
  label: string;
  menuItem: MenuPermission;
  agCatPermissions?: CatPermissions;
  agCatPermissionsLibrary?: CatPermissions;
  circulationPermissions?: CirculationPermissions;
  illUserPermissions?: ILLUserPermissions;
  versoReportPermissions?: VersoReportGroup[];
  translatePreferences?: TranslatePreferences;
}

export interface CirculationPermissions {
  editPatronRecord: boolean;
  payFines: boolean;
  viewFinesHistory: boolean;
  itemsOutDeclareLost: boolean;
  itemsOutDeclareClaimsReturn: boolean;
  itemsOutRenew: boolean;
  patronHistoryViewPreviouslyCheckedOut: boolean;
  addPeopleToHouseHoldRecord: boolean;
  notesAddEditUserNotes: boolean;
  finesFeesProcessPaymentsCredits: boolean;
  reservesChangePickupLocation: boolean;
  reservesEditReserveNotes: boolean;
  reservesDeleteOrSuspendReserves: boolean;
  reservesChangeReserveQueueSequence: boolean;
  reservesDeleteReserves: boolean;
}

/** CAT Staff AGCatPermissions */
export interface CatPermissions {
  catPermId: number;
  readonly catPermType: string;
  libraryList: string[];
  lpk: number;
  updateBib: 'Y' | 'M' | 'L' | 'N';
  updateHLD: 'Y' | 'M' | 'L' | 'N';
  username: string;
  hasAGCatPermissions: boolean;
  hasHMPermissions: boolean;
}

export interface VersoReport {
  id: number;
  key: string;
  name: string;
  description: string;
  type: string;
  sortOrder: number;
  selected: boolean;
}

export interface VersoReportGroup {
  label: string;
  type: string;
  reports: VersoReport[];
}

export interface TranslatePreferences {
  canTranslate: boolean;
  hasAuthority: boolean;
  hasTranslate: boolean;
  locSubjectAuthorities: string[];
  nameAuthorities: string[];
  subjectAuthorities: number[];
}

export interface ILLUserPermissions {
  pRequestToBorrow: boolean;
  pRequestForCopy: boolean;
  pPatronReqTracking: boolean;
  pOrigReqForInfo: boolean;
  pAutoApprove: boolean;
  pApproveRequest: boolean;
  pViewOwnRequests: boolean;
  pCancelOrChangeRequests: boolean;
  pRespondToRequest: boolean;
  pChangeOwnILLInfo: boolean;
  pChangeAnyILLInfo: boolean;
  pEditHoldMethod: boolean;
  pChangePickupLocation: boolean;
  pAllowUndoShipped: boolean;
}

export interface UserPermissions {
  circulationPermissions: CirculationPermissions;
  agCatPermissions: CatPermissions;
  agCatPermissionsLibrary: CatPermissions;
  versoReports: VersoReportGroup[];
  translatePreferences: TranslatePreferences;
  illUserPermissions: ILLUserPermissions;
}

/**
 * User Role Permissions for Menu Items and feature level permissions
 */
export interface UserRolePermissions {
  menuItems: StaffMenuItem[];
  permissions: UserPermissions;
}

class DuplicateResult<T> {
  value: T;
  matchedOn: boolean;
}

export class DuplicateCheckResponse {
  userId: number;
  username: DuplicateResult<string>;
  firstName: DuplicateResult<string>;
  lastName: DuplicateResult<string>;
  phone: DuplicateResult<string>;
  email: DuplicateResult<string>;
  address1: DuplicateResult<string>;
  dateOfBirth: DuplicateResult<Date>;
}

export interface NotificationPreferences {
  mobileProvider: number;
  notificationDay: string;
  notificationMethod: number;
  reserveNotificationMethod: number;
  sendPreOverdueNotices: boolean;
}

export interface NotificationPreferencesData {
  emailNotificationOptionEnabled: boolean;
  smsTextNotificationEnabled: boolean;
  sendPreOverdueNoticesEnabled: boolean;
  notificationPreferencesEnabled: boolean;
  reserveNotificationPreferencesEnabled: boolean;
  hasEmailAddress: boolean;
  hasMobilePhoneNumber: boolean;
  hasPrimaryPhoneNumber: boolean;
  fields: HtmlField[];
}

export interface UserPreferences {
  showPreviousCheckedOutItems: boolean;
}

export interface UserPreferencesFormData {
  fields: HtmlField[];
}

export interface NewUserPageData {
  userHtmlFields: HtmlField[][];
  patronNumericPassword: boolean;
  userDetails: UserDetails;
}

export interface EditUserPageData extends NewUserPageData {
  profilePhoto: string;
  profilePhotoEnabled: boolean;
  addedDate: Date;
  updatedDate: Date;
  showNotificationsLink: boolean;
  showPreferencesLink: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export interface UserQuickSearch {
  searchTerm: string;
  pageNumber: number;
  pageSize: number;
  userTypes: string[];
  status: string;
}

export interface SubmitSearchRequest {
  barcodeName: string;
  searchTerm: string;
  searchIndex: PATRON_SEARCH_INDEX;
  pageNumber: number;
  pageSize: number;
  userType: string;
}
export interface PatronGroup {
  id: number;
  groupName: string;
}
export interface PatronCategory {
  id: number;
  name: string;
}

export interface UserDetails {
  id: number;
  library: LibraryInfo;
  username: string;
  userRoles: string[];
  password: string;
  question: string;
  homeLocation: Location;
  firstName: string;
  initial: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  email: string;
  phone: string;
  phone2: string;
  fax: string;
  status: string;
  patronGroup: PatronGroup;
  patronCategory: PatronCategory;
  uniqueId: string;
  mobilePhone: string;
  workPhone: string;
  blocked: boolean;
  blockedReason: string;
  expireDate: Date;
  sponsor: string;
  comment: string;
  addedDate: Date;
  updatedDate: Date;
  notes: string;
  fineBalance: number;
  dateOfBirth: Date;
  message: string;
  nickname: string;
  foreignKey: string;
  lDF1: string;
  lDF2: string;
  lDF3: string;
  lDF4: string;
  lDF5: string;
  lDF6: string;
  lDF7: string;
  lDF8: string;
  lDF9: string;
  lDF10: string;
  lDF11: string;
  lDF12: string;
  lDF13: string;
  lDF14: string;
  lDF15: string;
  lDF16: string;
  lDF17: string;
  lDF18: string;
  lDF19: string;
  lDF20: string;
}

export enum USER_PERMISSIONS_MENU_ID {
  PAC_ADMIN = 3,
  ILL_ADMIN = 4,
  USER_ADMIN = 6,
  CAT_ADMIN = 22,
  CIRC_ADMIN = 23,
  CIRCULATION = 24,
  CIRC_REPORTS = 25,
  SERIALS = 26,
  ACQUISITIONS = 28,
  STATISTICS = 30,
  SUPPORT = 31,
}

export enum USER_STATUS {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

export enum PATRON_SEARCH_INDEX {
  Default,
  Barcode,
  Lastname,
  Firstname,
  Address1,
  City,
  ZipCode,
  Email,
  AnyPhone,
  Comment,
  Notes,
  LDF1,
  LDF2,
  LDF3,
  LDF4,
  LDF5,
  LDF6,
  LDF7,
  LDF8,
  LDF9,
  LDF10,
  LDF11,
  LDF12,
  LDF13,
  LDF14,
  LDF15,
  LDF16,
  LDF17,
  LDF18,
  LDF19,
  LDF20,
}

export interface UserFormRequest {
  username: string;
  password: string;
  ssn: string;
  question: string;
  homeLocationId: number;
  firstName: string;
  initial: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  email: string;
  phone: string;
  phone2: string;
  fax: string;
  status: string;
  patronGroupId: number;
  patronCategoryId: number;
  uniqueId: string;
  mobilePhone: string;
  workPhone: string;
  blocked: boolean;
  blockReason: string;
  expireDate: Date | null;
  sponsor: string;
  comment: string;
  addedDate: Date | null;
  updatedDate: Date | null;
  notes: string;
  fineBalance: number;
  dateOfBirth: Date | null;
  message: string;
  nickname: string;
  foreignKey: string;
  ldf1: string;
  ldf2: string;
  ldf3: string;
  ldf4: string;
  ldf5: string;
  ldf6: string;
  ldf7: string;
  ldf8: string;
  ldf9: string;
  ldf10: string;
  ldf11: string;
  ldf12: string;
  ldf13: string;
  ldf14: string;
  ldf15: string;
  ldf16: string;
  ldf17: string;
  ldf18: string;
  ldf19: string;
  ldf20: string;
  updateFamilyMemberAddress: boolean;
  isInstitution: boolean;
}

export interface UserSearchRequest {
  barcodeName?: string;
  searchTerm?: string;
  searchIndex?: PATRON_SEARCH_INDEX;
  userRole?: string;
  patronGroupId?: number;
  currentLibrary?: boolean;
  offset: number;
  limit: number;
}

export interface SaveMenuPermissionsRequest {
  userId: number;
  menuPermissions: MenuPermission[];
}

export interface SaveMenuPermissionRequest {
  userId: number;
  menuId: number;
  menuItemIds: number[];
}

export interface UpdateCirculationPermissionsRequest {
  userId: number;
  permissions: CirculationPermissions;
}

export interface UpdateCatPermissionsRequest {
  userId: number;
  permissions: CatPermissions;
}

export interface UpdateVersoReportsRequest {
  userId: number;
  reportConstantList: string;
}

export interface UpdateTranslatePreferencesRequest {
  userId: number;
  preferences: TranslatePreferences;
}

export interface UpdateILLUserPermissionsRequest {
  userId: number;
  permissions: ILLUserPermissions;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _apiService = inject(ApiService);
  private _libraryService = inject(LibraryService);
  private _configService = inject(ConfigService);

  private readonly _userApi = `${this._configService.agApi}/user`;

  private _libraries = new BehaviorSubject<Library[]>(null);
  libraries$ = this._libraries.asObservable();

  constructor() {
    this._libraryService.getLibraries().subscribe((libraries) => {
      this._libraries.next(libraries);
    });
  }

  isSingleLibraryInstitution$ = this.libraries$.pipe(
    filter((libs): libs is Library[] => !!libs),
    map((libraries) => libraries.length === 1),
  );

  /**
   * Returns the inital page data for a new user form
   * @param userType
   * userType - P for patron, S for staff
   * @returns Observable<any> get user form
   */
  getNewUserPageData(initialUserRole: string = 'P'): Observable<NewUserPageData> {
    return this._apiService.get<NewUserPageData>(`${this._userApi}/pagedata/newuser`, {
      params: {
        initialUserRole,
      },
    });
  }

  /**
   * Returns the page data for and existing user
   * @param userId
   * @returns Observable<any> get user form
   */
  getUserEditPageData(userId: number): Observable<EditUserPageData> {
    return this._apiService.get<EditUserPageData>(`${this._userApi}/${userId}/pagedata/edituser`, {
      params: {
        ...(userId > 0 && { userId: userId }),
      },
    });
  }

  /** Creates a new user with an initial user role; S or P */
  addUser(userRequest: Partial<UserFormRequest>, initialUserRole: string): Observable<number> {
    return this._apiService.post(`${this._userApi}`, userRequest, {
      params: { initialUserRole },
    });
  }

  /**
   * Edits an existing user
   * @param data - add user request information
   * @returns Observable<any> - add user response which should be 204 - NoContentResult
   */
  updateUser(userRequest: Partial<UserFormRequest>, userId: number): Observable<number> {
    return this._apiService.put(`${this._userApi}/${userId}`, userRequest);
  }

  getUserProfilePhoto(userId: number): Observable<string> {
    return this._apiService.get(`${this._userApi}/${userId}/profilephoto`);
  }

  saveUserProfilePhoto(userId: number, base64PhotoData: string): Observable<string> {
    return this._apiService.post(`${this._userApi}/${userId}/profilephoto`, { base64PhotoData });
  }

  getNotificationPreferences(userId: number): Observable<NotificationPreferences> {
    return this._apiService.get(`${this._userApi}/${userId}/preferences/notification`);
  }

  saveNotificationPreferences(userId: number, settings: NotificationPreferences): Observable<void> {
    return this._apiService.post(`${this._userApi}/${userId}/preferences/notification`, settings);
  }

  getNotificationPreferencesForm(userId: number): Observable<NotificationPreferencesData> {
    return this._apiService.get(`${this._userApi}/${userId}/preferences/notification/form`);
  }

  getUserPreferences(userId: number): Observable<UserPreferences> {
    return this._apiService.get(`${this._userApi}/${userId}/preferences/user`);
  }
  saveUserPreferences(userId: number, userPreferences: UserPreferences) {
    return this._apiService.post(`${this._userApi}/${userId}/preferences/user`, userPreferences);
  }
  getUserPreferencesForm(userId: number): Observable<UserPreferencesFormData> {
    return this._apiService.get(`${this._userApi}/${userId}/preferences/user/form`);
  }

  /**
   *
   * @param data - duplicate user check
   * @returns Observable<any> - get list of duplicate users if they exists, else 204 - NoContent
   */
  duplicateCheckList(data): Observable<DuplicateCheckResponse[]> {
    return this._apiService.post(`${this._userApi}/searchduplicates`, data);
  }

  /** Returns all the roles available to a user with hasRole true where the user has the given role
   * and Editable if the user can edit it.
   */
  getRoleAccessByUser(userId: number): Observable<UserRoleAccess[]> {
    return this._apiService.get<UserRoleAccess[]>(`${this._userApi}/roleaccess`, {
      params: { userId },
    });
  }

  /** Returns all grantable roles available to a library */
  getRoles(): Observable<UserRole[]> {
    return this._apiService.get<UserRole[]>(`${this._userApi}/roles`);
  }

  /** Returns roles assigned to a user */
  getRolesByUser(userId: number): Observable<UserRole[]> {
    return this._apiService.get<UserRole[]>(`${this._userApi}/${userId}/role`);
  }

  /** Add a single role to a given user */
  addRoleByUser(userId: number, role: string): Observable<any> {
    return this._apiService.put(`${this._userApi}/${userId}/role/${role}`, null);
  }

  addRolesByUser(userId: number, roles: string[]): Observable<any> {
    return this._apiService.post(`${this._userApi}/${userId}/role`, roles);
  }

  /** Removes a single role from a give user */
  removeRoleByUser(userId: number, role: string): Observable<any> {
    return this._apiService.delete(`${this._userApi}/${userId}/role/${role}`);
  }

  /**
   * Returns all UserFeatureSettings for a given user, based on the roles and features enabled.
   * All menuitems will be returned with hasPermission true where the user has the given permission
   */
  getUserFeatureSettings(userId: number): Observable<UserFeatureSettings[]> {
    return this._apiService.get<UserFeatureSettings[]>(
      `${this._userApi}/${userId}/featuresettings`,
    );
  }

  /** Saves all UserFeatureSettings to a given user */
  saveUserFeatureSettings(
    userId: number,
    userModuleSettings: UserFeatureSettings[],
  ): Observable<UserFeatureSettings[]> {
    return this._apiService.post(`${this._userApi}/${userId}/featuresettings`, userModuleSettings);
  }

  /** Returns the Home Location for a user */
  getHomeLocation(userId: number): Observable<Location> {
    return this._apiService.get<Location>(`${this._userApi}/${userId}/homelocation`);
  }

  /** Sets the Home Location for a user */
  setHomeLocation(userId: number, locId: number): Observable<any> {
    return this._apiService.post(`${this._userApi}/${userId}/homelocation/${locId}`);
  }

  /**
   * Returns all Locations associated with a user.
   * @requires Verso Staff
   */
  getHomeUserLocations(userId: number): Observable<UserLocation[]> {
    return this._apiService.get<UserLocation[]>(`${this._userApi}/${userId}/homelocations`);
  }

  /**
   * Saves / updates user's home locations.
   * @requires Verso Staff
   */
  saveHomeUserLocations(userId: number, userLocations: UserLocation[]): Observable<Location> {
    return this._apiService.post(`${this._userApi}/${userId}/homelocations`, userLocations);
  }

  /**
   * Returns all Libraries assigned to a user.
   * @requires Verso Staff
   */
  getUserRegionalLibraries(userId: number): Observable<LibraryInfo[]> {
    return this._apiService.get<LibraryInfo[]>(`${this._userApi}/${userId}/regionallibraries`);
  }

  /**
   * Saves / updates user's assigned libraries.
   * @requires Verso Staff
   */
  saveUserRegionalLibraries(userId: number, lpks: number[]): Observable<void> {
    return this._apiService.post(`${this._userApi}/${userId}/regionallibraries`, lpks);
  }

  cloneUserAccess(sourceUserId: number, targetUserId: number): Observable<boolean> {
    return this._apiService.post<boolean>(`${this._userApi}/cloneuseraccess`, null, {
      params: { sourceUserId, targetUserId },
    });
  }

  /**
   * User search
   * @param data - payload to fetch users based on payload
   * @returns Observable<any> - get list of users
   */
  getUser(userId: number): Observable<UserDetails> {
    return this._apiService.get(`${this._userApi}/${userId}`);
  }

  /***
   * Delete user
   * @param userId - user id to delete
   * @returns Observable<any> - delete user
   */
  deleteUser(userId: number): Observable<any> {
    return this._apiService.delete(`${this._userApi}/${userId}`);
  }

  /**
   * User search
   * @param data - payload to fetch users based on payload
   * @returns Observable<any> - get list of users
   */
  searchUsers(data: UserSearchRequest): Observable<UserDetails[]> {
    return this._apiService.post(`${this._userApi}/lookup`, data);
  }

  /**
   * Staff user search (searches only for users with role 'S')
   * @param data - payload to fetch staff users based on payload
   * @returns Observable<UserDetails[]> - get list of staff users
   */
  searchStaffUsers(data: UserSearchRequest): Observable<UserDetails[]> {
    return this._apiService.post(`${this._userApi}/stafflookup`, data);
  }

  /**
   * A smart, quick search for users
   * @param searchTerm
   * @returns
   */
  getUsersQuickSearch(
    searchTerm: string,
    userRoles: string[] = null,
    page = 1,
  ): Observable<UserDetails[]> {
    if (searchTerm === '' || searchTerm == null) {
      return of([]);
    }
    return this._apiService.post<UserDetails[]>(`${this._userApi}/quicksearch`, {
      searchTerm: searchTerm,
      userRoles: userRoles,
      status: 'A',
      pageNumber: page,
      pageSize: 10,
    });
  }

  isEmailAvailable(email: string, userId: number = 0): Observable<boolean> {
    const url = this._configService.agApi + '/user/emailAvailable';
    return this._apiService.post(url, null, { params: { email, userId } });
  }

  isUsernameAvailable(uniqueId: string, userId: number = 0): Observable<boolean> {
    const url = this._configService.agApi + '/user/usernameAvailable';
    return this._apiService.post(url, null, { params: { uniqueId, userId } });
  }

  getAdvancedUserSearchForm(): Observable<ODataSearchableField[]> {
    const url = this._configService.agApi + '/user/advancedsearch';
    return this._apiService.get<ODataSearchableField[]>(url);
  }

  advancedUserSearch(data: ODataQuery): Observable<UserDetails[]> {
    const url = this._configService.agApi + '/user/advancedsearch';
    return this._apiService.post(url, data);
  }
}
